import React from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, DollarSign } from 'lucide-react';
import { FinanceiroCard } from './index';
import { getCompanyContext } from 'utils/setCompanyContext';
import { normalizeCompanyName } from 'utils/companyNormalizer';
import Modal from 'components/common/Modal';

const FILTRO_MENSALIDADES_STORAGE_KEY = 'financeiro_mensalidades_filtro';

const KPISection = ({ dadosReais }) => {
  const [isClassificacaoModalOpen, setIsClassificacaoModalOpen] = React.useState(false);
  const [filtroModal, setFiltroModal] = React.useState(() => {
    try {
      const valorSalvo = window.localStorage.getItem(FILTRO_MENSALIDADES_STORAGE_KEY);
      if (['todas', 'parcelado', 'nao_parcelado'].includes(valorSalvo)) {
        return valorSalvo;
      }
    } catch (error) {
      // Ignorar problemas de acesso ao storage
    }
    return 'todas';
  });
  const empresaAtual = getCompanyContext()?.companies?.[0]?.name || '';
  const isMovFit = normalizeCompanyName(empresaAtual) === 'mov fit';

  // Usar dados reais da API
  // Preferir campos específicos quando disponíveis
  const entradasBrutas = (dadosReais.entradas_brutas ?? dadosReais.entradas) || 0;
  const entradasLiquidas = dadosReais.entradas_liquidas || 0;
  const saidas = dadosReais.saidas || 0;
  const caixaLiquido = dadosReais.caixa_liquido || 0;
  
  // Variações
  const entradasVariacao = dadosReais.entradas_variacao_pct || 0;
  const saidasVariacao = dadosReais.saidas_variacao_pct || 0;

  const classificacaoRecebimento = React.useMemo(() => {
    const lista = dadosReais?.classificacao_recebimento;
    if (!Array.isArray(lista)) return [];

    return lista
      .filter((item) => item && typeof item === 'object')
      .map((item) => ({
        unidade: String(item.unidade || item.unidade_nome || 'Não informada'),
        descricaoParcela: String(item.descricaoParcela || item.descricao_parcela || ''),
        planoRaw: item.plano,
        descricaoPlano: String(item.descricaoPlano || item.descricao_plano || ''),
        nomePlano: String(item.nome_plano || ''),
        valor: parseFloat(item.valor) || 0,
        quantidade: Math.max(1, parseInt(item.quantidade, 10) || 1)
      }));
  }, [dadosReais?.classificacao_recebimento]);

  const resumoClassificacao = React.useMemo(() => {
    let entradasBrutasResumo = 0;
    let totalTitulosEntradasBrutas = 0;
    let totalValorParcelado = 0;
    let totalVendaAvulsa = 0;

    const grupos = classificacaoRecebimento.reduce((acc, item) => {
      const descricaoParcelaOriginal = (item.descricaoParcela || '').trim();
      const descricaoParcela = descricaoParcelaOriginal.toUpperCase();
      const valor = Number(item.valor) || 0;
      const quantidade = Number(item.quantidade) || 1;

      const planoBase = (
        (item.planoRaw && typeof item.planoRaw === 'object')
          ? (item.planoRaw.nome || item.planoRaw.descricao || '')
          : (item.planoRaw || item.descricaoPlano || item.nomePlano || '')
      );

      const plano = planoBase
        ? String(planoBase)
        : (descricaoParcela === 'VENDA AVULSA'
          ? 'VENDA AVULSA'
          : (descricaoParcelaOriginal || 'SEM PLANO'));

      const isVenda = descricaoParcela.includes('VENDA') || plano.toUpperCase().includes('VENDA');
      if (isVenda) {
        totalVendaAvulsa += valor;
        return acc; // Modal deve mostrar somente entradas brutas
      }

      entradasBrutasResumo += valor;
      totalTitulosEntradasBrutas += quantidade;
      if (plano.toUpperCase().includes('PARCELADO') || descricaoParcela.includes('PARCELADO')) {
        totalValorParcelado += valor;
      }

      const chave = item.unidade || 'Não informada';
      if (!acc[chave]) {
        acc[chave] = {
          unidade: chave,
          recebidoPorPlanoMap: {},
          quantidadeTotal: 0,
          valorTotal: 0
        };
      }

      if (!acc[chave].recebidoPorPlanoMap[plano]) {
        acc[chave].recebidoPorPlanoMap[plano] = {
          plano,
          valor: 0,
          quantidade: 0
        };
      }

      acc[chave].recebidoPorPlanoMap[plano].valor += valor;
      acc[chave].recebidoPorPlanoMap[plano].quantidade += quantidade;

      acc[chave].quantidadeTotal += quantidade;
      acc[chave].valorTotal += valor;
      return acc;
    }, {});

    const unidades = Object.values(grupos)
      .map((grupo) => ({
        ...grupo,
        recebidoPorPlano: Object.values(grupo.recebidoPorPlanoMap)
          .sort((a, b) => b.valor - a.valor)
      }))
      .sort((a, b) => a.unidade.localeCompare(b.unidade, 'pt-BR'));

    return {
      unidades,
      entradasBrutas: entradasBrutasResumo,
      totalTitulosRecebidos: totalTitulosEntradasBrutas,
      totalValorParcelado,
      totalVendaAvulsa
    };
  }, [classificacaoRecebimento]);

  const mensalidadesClicavel = isMovFit && resumoClassificacao.totalTitulosRecebidos > 0;
  const totalValorNaoParcelado = Math.max(0, resumoClassificacao.entradasBrutas - resumoClassificacao.totalValorParcelado);
  const valorMensalidadesSelecionado = React.useMemo(() => {
    if (!isMovFit) return entradasBrutas;
    if (filtroModal === 'parcelado') return resumoClassificacao.totalValorParcelado;
    if (filtroModal === 'nao_parcelado') return totalValorNaoParcelado;
    return resumoClassificacao.entradasBrutas;
  }, [
    isMovFit,
    filtroModal,
    entradasBrutas,
    resumoClassificacao.entradasBrutas,
    resumoClassificacao.totalValorParcelado,
    totalValorNaoParcelado
  ]);
  const entradasLiquidasExibicao = React.useMemo(() => {
    const temSelecaoMensalidades = isMovFit && classificacaoRecebimento.length > 0;
    if (!temSelecaoMensalidades) {
      return entradasLiquidas;
    }

    return (valorMensalidadesSelecionado || 0) + (resumoClassificacao.totalVendaAvulsa || 0);
  }, [
    isMovFit,
    classificacaoRecebimento.length,
    entradasLiquidas,
    valorMensalidadesSelecionado,
    resumoClassificacao.totalVendaAvulsa
  ]);
  const caixaLiquidoExibicao = React.useMemo(() => {
    const temSelecaoMensalidades = isMovFit && classificacaoRecebimento.length > 0;
    if (!temSelecaoMensalidades) {
      return caixaLiquido;
    }

    return (entradasLiquidasExibicao || 0) - (saidas || 0);
  }, [
    isMovFit,
    classificacaoRecebimento.length,
    caixaLiquido,
    entradasLiquidasExibicao,
    saidas
  ]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(FILTRO_MENSALIDADES_STORAGE_KEY, filtroModal);
    } catch (error) {
      // Ignorar problemas de acesso ao storage
    }
  }, [filtroModal]);
  const classificacaoFiltrada = React.useMemo(() => {
    const matchParcelado = (plano) => String(plano || '').toUpperCase().includes('PARCELADO');

    const unidades = resumoClassificacao.unidades
      .map((grupo) => {
        let itens = grupo.recebidoPorPlano;
        if (filtroModal === 'parcelado') {
          itens = itens.filter((item) => matchParcelado(item.plano));
        } else if (filtroModal === 'nao_parcelado') {
          itens = itens.filter((item) => !matchParcelado(item.plano));
        }

        const quantidadeTotal = itens.reduce((acc, item) => acc + (Number(item.quantidade) || 0), 0);
        const valorTotal = itens.reduce((acc, item) => acc + (Number(item.valor) || 0), 0);

        return {
          ...grupo,
          recebidoPorPlano: itens,
          quantidadeTotal,
          valorTotal
        };
      })
      .filter((grupo) => grupo.recebidoPorPlano.length > 0);

    const totalTitulos = unidades.reduce((acc, grupo) => acc + (grupo.quantidadeTotal || 0), 0);
    return { unidades, totalTitulos };
  }, [filtroModal, resumoClassificacao.unidades]);

  const cardFiltroClass = (ativo) =>
    `border rounded-lg p-3 transition-all text-left w-full ${
      ativo
        ? 'border-blue-500 bg-[color:var(--surface)] ring-1 ring-blue-500/30 shadow-[0_0_0_1px_rgba(59,130,246,0.2)]'
        : 'border-[color:var(--border)] bg-[color:var(--surface-muted)] hover:bg-[color:var(--surface)]'
    }`;
  const labelFiltroMensalidades = React.useMemo(() => {
    if (filtroModal === 'parcelado') return 'Parcelado';
    if (filtroModal === 'nao_parcelado') return 'Não Parcelado';
    return 'Entradas Brutas';
  }, [filtroModal]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <FinanceiroCard
          titulo={isMovFit ? `Mensalidades (${labelFiltroMensalidades})` : 'Entradas Brutas'}
          valor={`R$ ${valorMensalidadesSelecionado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          subtitulo={isMovFit ? (mensalidadesClicavel ? `Selecionado: ${labelFiltroMensalidades} • Clique para alterar` : 'Receita de mensalidades') : null}
          variacao={entradasVariacao !== 0 ? `${entradasVariacao}% vs mês anterior` : null}
          cor="green"
          icone={TrendingUp}
          status={entradasVariacao > 0 ? 'positivo' : 'negativo'}
          clicavel={mensalidadesClicavel}
          onClick={mensalidadesClicavel ? () => {
            setIsClassificacaoModalOpen(true);
          } : undefined}
        />
        
        <FinanceiroCard
          titulo={isMovFit ? 'Mensalidades + Produtos' : 'Entradas Líquidas'}
          valor={`R$ ${entradasLiquidasExibicao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          subtitulo={isMovFit ? 'Mensalidades + produtos vendidos' : null}
          variacao={entradasVariacao !== 0 ? `${entradasVariacao}% vs mês anterior` : null}
          cor="blue"
          icone={DollarSign}
          status={entradasVariacao > 0 ? 'positivo' : 'negativo'}
        />
        
        <FinanceiroCard
          titulo="Saídas"
          valor={`R$ ${saidas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          variacao={saidasVariacao !== 0 ? `${saidasVariacao}% vs mês anterior` : null}
          cor="red"
          icone={TrendingDown}
          status={saidasVariacao < 0 ? 'positivo' : 'negativo'}
        />
        
        <FinanceiroCard
          titulo="Caixa Líquido"
          valor={`R$ ${caixaLiquidoExibicao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          variacao={caixaLiquidoExibicao > 0 ? 'Positivo' : 'Negativo'}
          tipoVariacao="texto"
          cor="amber"
          icone={AlertTriangle}
          status={caixaLiquidoExibicao > 0 ? 'positivo' : 'negativo'}
        />
      </div>

      <Modal
        isOpen={isClassificacaoModalOpen}
        onClose={() => setIsClassificacaoModalOpen(false)}
        title="Classificação de Recebimento"
        size="lg"
      >
        <div className="max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <button type="button" onClick={() => setFiltroModal('todas')} className={cardFiltroClass(filtroModal === 'todas')}>
              <p className="text-xs text-[color:var(--text-primary)]/75 uppercase">Entradas Brutas</p>
              <p className={`text-base font-semibold ${filtroModal === 'todas' ? 'text-blue-400' : 'text-[color:var(--text-primary)]'}`}>
                R$ {resumoClassificacao.entradasBrutas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </button>
            <button type="button" onClick={() => setFiltroModal('parcelado')} className={cardFiltroClass(filtroModal === 'parcelado')}>
              <p className="text-xs text-[color:var(--text-primary)]/75 uppercase">Plano Anual Parcelado</p>
              <p className={`text-base font-semibold ${filtroModal === 'parcelado' ? 'text-blue-400' : 'text-[color:var(--text-primary)]'}`}>
                R$ {resumoClassificacao.totalValorParcelado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </button>
            <button type="button" onClick={() => setFiltroModal('nao_parcelado')} className={cardFiltroClass(filtroModal === 'nao_parcelado')}>
              <p className="text-xs text-[color:var(--text-primary)]/75 uppercase">Planos não parcelados</p>
              <p className={`text-base font-semibold ${filtroModal === 'nao_parcelado' ? 'text-blue-400' : 'text-[color:var(--text-primary)]'}`}>
                R$ {totalValorNaoParcelado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </button>
          </div>

          <div className="space-y-4">
            {classificacaoFiltrada.unidades.map((grupo) => (
              <div key={grupo.unidade} className="border border-[color:var(--border)] rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-[color:var(--surface-muted)] border-b border-[color:var(--border)] flex items-center justify-between">
                  <p className="text-sm font-semibold text-[color:var(--text-primary)]">{grupo.unidade}</p>
                  <p className="text-xs text-[color:var(--text-muted)]">
                    {grupo.quantidadeTotal.toLocaleString('pt-BR')} itens • R$ {grupo.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <table className="w-full">
                  <thead className="bg-[color:var(--surface)]">
                    <tr className="border-b border-[color:var(--border)]">
                      <th className="px-3 py-2 text-xs font-semibold text-[color:var(--text-primary)]/80 uppercase tracking-wider text-left">Plano</th>
                      <th className="px-3 py-2 text-xs font-semibold text-[color:var(--text-primary)]/80 uppercase tracking-wider text-right">Quantidade</th>
                      <th className="px-3 py-2 text-xs font-semibold text-[color:var(--text-primary)]/80 uppercase tracking-wider text-right">Valor (R$)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[color:var(--border)]">
                    {grupo.recebidoPorPlano.map((item, index) => (
                      <tr key={`${grupo.unidade}-${item.plano}-${index}`} className="hover:bg-[color:var(--surface-muted)] transition-colors">
                        <td className="px-3 py-2 text-sm text-[color:var(--text-primary)]">{item.plano}</td>
                        <td className="px-3 py-2 text-sm text-[color:var(--text-primary)] text-right">{item.quantidade.toLocaleString('pt-BR')}</td>
                        <td className="px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] text-right">
                          {item.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[color:var(--border)] text-sm text-[color:var(--text-muted)] text-center">
          Total de {classificacaoFiltrada.totalTitulos.toLocaleString('pt-BR')} {classificacaoFiltrada.totalTitulos === 1 ? 'título' : 'títulos'} em {classificacaoFiltrada.unidades.length} {classificacaoFiltrada.unidades.length === 1 ? 'unidade' : 'unidades'}
        </div>
      </Modal>
    </>
  );
};

export default KPISection;
